import { Badge, BodyText, Button } from '@postscript/components';
import { ComponentProps } from 'react';
import styled, { css } from 'styled-components';

interface PromoTag {
  label: string;
  variant?: ComponentProps<typeof Badge>['variant'];
}

type PromoOrientation = 'vertical' | 'horizontal';

interface LaunchpadPromoCardProps {
  title: string;
  description: string;
  tags?: PromoTag[];
  imageUrl?: string;
  orientation?: PromoOrientation;
  onLearnMore?: () => void;
  onDismiss?: () => void;
}

const StyledCard = styled.div<{ $orientation: PromoOrientation }>`
  display: flex;
  background: var(--surface-bkg-color);
  border-radius: var(--border-radius-small);
  overflow: hidden;
  width: 100%;

  ${({ $orientation }) =>
    $orientation === 'vertical'
      ? css`
          flex-direction: column;
          align-items: flex-start;
        `
      : css`
          flex-direction: row;
          align-items: stretch;
        `}
`;

const StyledImageArea = styled.div<{ $orientation: PromoOrientation }>`
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background: var(--gray-2);
  position: relative;

  ${({ $orientation }) =>
    $orientation === 'vertical'
      ? css`
          width: 100%;
          min-height: 80px;
          border-radius: var(--border-radius-small) var(--border-radius-small) 0
            0;
        `
      : css`
          width: 120px;
          flex-shrink: 0;
          border-radius: var(--border-radius-small) 0 0
            var(--border-radius-small);
        `}
`;

const StyledImage = styled.img`
  max-width: 100%;
  height: auto;
`;

const StyledImageTagRow = styled.div`
  display: flex;
  gap: var(--spacing-1);
  flex-wrap: wrap;
  position: absolute;
  bottom: var(--spacing-1);
  left: var(--spacing-1);
`;

const StyledInlineTagRow = styled.div`
  display: flex;
  gap: var(--spacing-1);
  flex-wrap: wrap;
`;

const StyledTextContent = styled.div<{
  $orientation: PromoOrientation;
  $hasImage: boolean;
}>`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-1);
  padding: var(--spacing-2);
  border: 1px solid var(--border-color-dim);

  ${({ $orientation, $hasImage }) =>
    $orientation === 'vertical'
      ? css`
          width: 100%;
          border-top: ${$hasImage
            ? 'none'
            : '1px solid var(--border-color-dim)'};
          border-radius: ${$hasImage
            ? '0 0 var(--border-radius-small) var(--border-radius-small)'
            : 'var(--border-radius-small)'};
        `
      : css`
          flex: 1;
          min-width: 0;
          border-left: ${$hasImage
            ? 'none'
            : '1px solid var(--border-color-dim)'};
          border-radius: ${$hasImage
            ? '0 var(--border-radius-small) var(--border-radius-small) 0'
            : 'var(--border-radius-small)'};
        `}
`;

const StyledActions = styled.div`
  display: flex;
  gap: var(--spacing-1);
  align-items: center;
  flex-wrap: wrap;
  padding-top: var(--spacing-1);
`;

const LaunchpadPromoCard: React.FC<LaunchpadPromoCardProps> = ({
  title,
  description,
  tags,
  imageUrl,
  orientation = 'vertical',
  onLearnMore,
  onDismiss,
}) => {
  const hasImage = !!imageUrl;

  return (
    <StyledCard $orientation={orientation} data-cy="launchpad-promo-card">
      {imageUrl && (
        <StyledImageArea $orientation={orientation}>
          <StyledImage src={imageUrl} alt="" />
          {orientation === 'vertical' && tags && tags.length > 0 && (
            <StyledImageTagRow>
              {tags.map((tag) => (
                <Badge key={tag.label} variant={tag.variant}>
                  {tag.label}
                </Badge>
              ))}
            </StyledImageTagRow>
          )}
        </StyledImageArea>
      )}

      <StyledTextContent $orientation={orientation} $hasImage={hasImage}>
        {(!imageUrl || orientation === 'horizontal') &&
          tags &&
          tags.length > 0 && (
            <StyledInlineTagRow>
              {tags.map((tag) => (
                <Badge key={tag.label} variant={tag.variant}>
                  {tag.label}
                </Badge>
              ))}
            </StyledInlineTagRow>
          )}

        <BodyText size="small" bold color="var(--text-color)">
          {title}
        </BodyText>
        <BodyText size="x-small">{description}</BodyText>

        {(onLearnMore || onDismiss) && (
          <StyledActions>
            {onLearnMore && (
              <Button variant="primary" size="small" onClick={onLearnMore}>
                Learn More
              </Button>
            )}
            {onDismiss && (
              <Button variant="secondary" size="small" onClick={onDismiss}>
                Dismiss
              </Button>
            )}
          </StyledActions>
        )}
      </StyledTextContent>
    </StyledCard>
  );
};

export default LaunchpadPromoCard;
